import React, { useState, useCallback, useEffect, useRef } from 'react';
import VideoCapture from './GestureToSpeech/VideoCapture';
import HeyGenAvatar from './GestureToSpeech/HeyGenAvatar';
import TranscriptPopup from './GestureToSpeech/TranscriptPopup';
import heygenSocketService from '../../utils/heygenSocketService';
import { PATTERNS, processWordBuffer, couldFormPattern } from '../../utils/aslPatternUtils';
import '../../styles/components/WebDemo/GestureToSpeech.css';

const GestureToSpeech = ({ setMode }) => {
  const [debugMode, setDebugMode] = useState(false);
  const [predictions, setPredictions] = useState({
    predictions: [],
    sentence: '',
    metrics: { avg_inference_time: 0, frames_processed: 0 }
  });

  // Track socket connections
  const [heygenConnected, setHeygenConnected] = useState(false);
  const [heygenError, setHeygenError] = useState(null);
  const [heygenStream, setHeygenStream] = useState(null);

  // For transcript and pattern matching
  const [allSentences, setAllSentences] = useState([]);
  const [showTranscript, setShowTranscript] = useState(false);
  const wordBufferRef = useRef([]);

  // Wrapper around heygenSocketService.speakWord to also track in transcript
  const speakFn = useCallback((phrase) => {
    console.log(`[GTS:${new Date().toISOString()}] Speaking: "${phrase}"`);
    
    // Handle special cases for #AI and #ASL
    const modifiedPhrase = phrase
      .split(' ')
      .map(word => {
        switch(word.toLowerCase()) {
          case '#ai':
            return 'Ey eye';
          case '#asl':
            return 'Ey ess ell';
          case 'hello':
            return 'Hello!';
          default:
            return word;
        }
      })
      .join(' ');

    heygenSocketService.speakWord(modifiedPhrase);
    setAllSentences(prev => [...prev, phrase]); // Store original phrase in transcript
  }, []);

  // Handle predictions from VideoCapture
  const handlePrediction = useCallback((message) => {
    const timestamp = new Date().toISOString();
    console.log(`[GTS:${timestamp}] Received prediction:`, message);
    
    setPredictions(message);

    // If we have at least one predicted word
    if (message.predictions?.length > 0) {
      const topWord = message.predictions[0].word.toLowerCase(); // Normalize to lowercase
      console.log(`[GTS:${timestamp}] Processing word: "${topWord}"`);

      if (topWord === 'idle') {
        // When we get 'idle', first try one last pattern match
        console.log(`[GTS:${timestamp}] Got idle, trying one last pattern match with buffer:`, [...wordBufferRef.current]);
        processWordBuffer(wordBufferRef.current, PATTERNS, speakFn);
        
        // Then speak all remaining words in a single request
        if (wordBufferRef.current.length > 0) {
          console.log(`[GTS:${timestamp}] Speaking remaining words in buffer as a single phrase:`, [...wordBufferRef.current]);
          const remainingPhrase = wordBufferRef.current.join(' ');
          speakFn(remainingPhrase);
          // Clear the buffer after speaking
          wordBufferRef.current = [];
        }
      } else {
        // Non-idle word handling
        // 1) Add the word to our buffer
        wordBufferRef.current.push(topWord);
        console.log(`[GTS:${timestamp}] Added word to buffer. Current buffer:`, [...wordBufferRef.current]);

        // 2) Try matching patterns from the start of the buffer
        console.log(`[GTS:${timestamp}] Attempting to match patterns with current buffer:`, [...wordBufferRef.current]);
        const beforeLength = wordBufferRef.current.length;
        processWordBuffer(wordBufferRef.current, PATTERNS, speakFn);
        const afterLength = wordBufferRef.current.length;
        console.log(`[GTS:${timestamp}] After pattern matching, buffer ${beforeLength > afterLength ? 'was' : 'was not'} reduced. Remaining:`, [...wordBufferRef.current]);
        
        // 3) If we still have words in the buffer, check if they could potentially form a pattern
        if (wordBufferRef.current.length > 0) {
          const canStillFormPattern = couldFormPattern(wordBufferRef.current, PATTERNS);
          
          // If buffer can't form any pattern, speak it immediately
          if (!canStillFormPattern) {
            console.log(`[GTS:${timestamp}] Buffer cannot form any pattern, speaking immediately:`, [...wordBufferRef.current]);
            const phrase = wordBufferRef.current.join(' ');
            speakFn(phrase);
            wordBufferRef.current = [];
          } else {
            console.log(`[GTS:${timestamp}] Buffer could still form a pattern, waiting for more words`);
          }
        }
      }
    }
  }, [speakFn]);

  const handleHeyGenMessage = useCallback((message) => {
    const timestamp = new Date().toISOString();
    console.log(`[GTS:${timestamp}] Received HeyGen message type: ${message.type}`);

    if (message.type === 'session_ready') {
      console.log(`[GTS:${timestamp}] HeyGen avatar is ready`);
      setHeygenError(null);
    } else if (message.type === 'error') {
      console.error(`[GTS:${timestamp}] HeyGen error:`, message.message);
      setHeygenError(message.message);
    } else if (message.type === 'media_stream') {
      console.log(`[GTS:${timestamp}] Received HeyGen media stream:`, {
        id: message.id,
        active: message.active,
        audioTracks: message.getAudioTracks().length,
        videoTracks: message.getVideoTracks().length
      });
      
      if (message.active && message.getVideoTracks().length > 0) {
        console.log(`[GTS:${timestamp}] Setting valid media stream with video`);
        setHeygenStream(message);
      } else {
        console.warn(`[GTS:${timestamp}] Received invalid or video-less stream`);
        setHeygenError('Received invalid video stream from avatar service');
      }
    }
  }, []);

  // Set up HeyGen WebSocket connection
  useEffect(() => {
    let isCurrentMount = true;
    console.log(`[GTS:${new Date().toISOString()}] Setting up HeyGen WebSocket connection`);
    
    const setupConnection = async () => {
      if (!isCurrentMount) return;

      // Set up HeyGen connection
      heygenSocketService.addListener('message', handleHeyGenMessage);
      heygenSocketService.addListener('media_stream', (stream) => {
        if (!isCurrentMount) return;
        
        const timestamp = new Date().toISOString();
        console.log(`[GTS:${timestamp}] Received media stream event:`, {
          id: stream.id,
          active: stream.active,
          audioTracks: stream.getAudioTracks().length,
          videoTracks: stream.getVideoTracks().length
        });
        
        if (stream.active && stream.getVideoTracks().length > 0) {
          console.log(`[GTS:${timestamp}] Setting valid media stream`);
          setHeygenStream(stream);
        } else {
          console.warn(`[GTS:${timestamp}] Received invalid media stream`);
          setHeygenError('Received invalid video stream from avatar service');
        }
      });
      heygenSocketService.addListener('error', (error) => {
        console.error('[GTS] HeyGen socket error:', error);
        if (isCurrentMount) {
          setHeygenError('Failed to connect to avatar server');
          setHeygenConnected(false);
        }
      });
      heygenSocketService.addListener('close', () => {
        console.log('[GTS] HeyGen socket closed');
        if (isCurrentMount) {
          setHeygenConnected(false);
        }
      });

      try {
        await heygenSocketService.connect();

        if (!isCurrentMount) return;

        console.log('[GTS] HeyGen WebSocket connection established');
        setHeygenConnected(true);
        setHeygenError(null);
      } catch (error) {
        if (!isCurrentMount) return;
        
        console.error('[GTS] Error establishing HeyGen connection:', error);
        setHeygenError('Failed to connect to avatar server');
      }
    };

    setupConnection();

    // Cleanup function
    return () => {
      isCurrentMount = false;
      console.log('[GTS] Component unmounting - cleaning up WebSocket connections');
      
      heygenSocketService.removeListener('message', handleHeyGenMessage);
      heygenSocketService.removeListener('media_stream', null); // Remove all media_stream listeners
      
      heygenSocketService.disconnect();
      setHeygenConnected(false);
    };
  }, [handleHeyGenMessage]);

  // Effect to handle stream cleanup
  useEffect(() => {
    return () => {
      if (heygenStream) {
        console.log(`[GTS:${new Date().toISOString()}] Cleaning up HeyGen stream`);
        heygenStream.getTracks().forEach(track => track.stop());
        setHeygenStream(null);
      }
    };
  }, [heygenStream]);

  return (
    <div className="gesture-to-speech-container">
      <div className="main-content">
        <VideoCapture 
          videoSource="/test.mp4"
          keypointsSource="/test_keypoints.json"
          showMetrics={debugMode} 
          predictions={predictions}
          isConnected={true}
          onPrediction={handlePrediction}
        />
        
        <div className="avatar-section">
          {heygenError ? (
            <div className="avatar-error">
              <div className="error-icon">⚠️</div>
              <div className="error-message">
                {heygenError}
                <button 
                  className="retry-button"
                  onClick={() => {
                    setHeygenError(null);
                    setHeygenStream(null);
                    // First disconnect and cleanup
                    heygenSocketService.disconnect();
                    // Then reconnect after a short delay to ensure cleanup is complete
                    setTimeout(() => heygenSocketService.connect(), 1000);
                  }}
                >
                  Retry Avatar Connection
                </button>
              </div>
            </div>
          ) : (
            <HeyGenAvatar 
              isConnected={heygenConnected} 
              mediaStream={heygenStream}
            />
          )}
        </div>
      </div>
      
      {/* Show transcript if user toggled it on */}
      {showTranscript && (
        <TranscriptPopup
          allSentences={allSentences}
          currentSentence={wordBufferRef.current}
          isTranslating={false}
          onClose={() => setShowTranscript(false)}
        />
      )}

      <div className="button-container">
        <button 
          className="mode-switch-button"
          onClick={() => setMode('speechToGesture')}
          disabled
          title="This functionality is not yet available"
        >
          Switch to Speech To ASL
        </button>
        <button 
          className={`debug-toggle-button ${debugMode ? 'active' : ''}`}
          onClick={() => setDebugMode(!debugMode)}
        >
          {debugMode ? 'Hide Debug Info' : 'Show Debug Info'}
        </button>
        <button 
          className={`transcript-toggle-button ${showTranscript ? 'active' : ''}`}
          onClick={() => setShowTranscript(!showTranscript)}
        >
          {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
        </button>
      </div>
    </div>
  );
};

export default GestureToSpeech;